/* eslint-disable react/prop-types */
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import React, { PureComponent } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import 'react-datepicker/dist/react-datepicker.css';
import FieldContainer from '../components/FieldContainer';
import { isFieldLocked } from '../components/utils';
import { formatDate } from '../utils/dateUtils';
import { clampToIntervals, computeIntervals, getDefaultInputFormats, getFilterTime, toWholeDays } from './dateField.utils';
import i18n from '../translations';

function isTime(value) {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
}

import { StringField } from './index';
import { locales } from '../utils/date';

// The calendar opens inside the field's <label>: a click on a day also "clicks" the input through the label, which
// opens the calendar again. Cancelling the label's default action lets the calendar close once a day is chosen.
const CalendarContainer = ({ children }) => <div onClick={(e) => e.preventDefault()}>{children}</div>;

const isAfterToday = (date) => {
  const currentDate = moment().format('YYYY-MM-DD');
  return moment(date).isSameOrAfter(currentDate);
};

function parseDateString(value, { showTimeSelect, showTimeSelectOnly, storeIsoDate }) {
  if (!value) return null;

  const today = moment().format('YYYY-MM-DD');
  if (storeIsoDate) {
    return moment(value, moment.ISO_8601).toDate();
  }
  if (showTimeSelectOnly) {
    return moment(`${today} ${value}`, 'YYYY-MM-DD HH:mm').toDate(); // hack: inject today with the selected time (HH:mm) because DatePicker need a date
  }
  if (showTimeSelect) {
    if (isTime(value)) {
      return moment(`${today} ${value}`, 'YYYY-MM-DD HH:mm').toDate(); // hack: inject today with the selected time (HH:mm) because DatePicker need a date
    }
    return moment(value, 'YYYY-MM-DD HH:mm').toDate();
  }
  return moment(value, 'YYYY-MM-DD').toDate();
}

function formatDateForSummary(value, { showTimeSelect, showTimeSelectOnly }) {
  if (!value || showTimeSelectOnly) return value;
  return formatDate(value, showTimeSelect ? 'LLLL' : 'LL');
}

export default class DateField extends PureComponent {
  render() {
    const {
      onChange,
      label,
      mode,
      name,
      value,
      showTimeSelect: showTimeSelectProp = false,
      showTimeSelectOnly = false,
      timeCaption,
      summary,
      includeDateIntervales = [],
      openToDate,
      timeIntervals = 10,
      storeIsoDate = false,
      lang,
      fromToday = false,
      dateFormat,
      placeholder,
      required,
      calendarStartDay,
      disabled,
      locked,
    } = this.props;

    // The picker only lists the hours when showTimeSelect is set: "time only" implies it
    const showTimeSelect = showTimeSelectProp || showTimeSelectOnly;
    const isLocked = isFieldLocked(name, locked);
    const formatLang = lang || i18n.language;
    const finalDateFormat = dateFormat || getDefaultInputFormats(formatLang, showTimeSelect);
    // The placeholder describes the default formats only
    const placeholderKey = showTimeSelectOnly ? 'time' : showTimeSelect ? 'date-time' : 'date';
    const placeholderText = placeholder || (dateFormat && !showTimeSelectOnly ? undefined : i18n.t(`dates.placeholder.${placeholderKey}`, { lng: formatLang }));

    if (mode === 'summary') {
      if (summary?.hidden) {
        return null;
      }

      const formattedDate = formatDateForSummary(value, { dateFormat, showTimeSelect, showTimeSelectOnly });
      return <StringField label={summary?.label || label} value={formattedDate} mode="summary" />;
    }

    const selected = parseDateString(value, { showTimeSelect, showTimeSelectOnly, storeIsoDate });

    const { hasIncludeIntervals, includeIntervals } = computeIntervals(includeDateIntervales, showTimeSelect);
    const defaultOpenToDate = hasIncludeIntervals && orderBy(includeIntervals, 'start', 'asc')?.[0]?.start;
    const finalOpenToDate = (openToDate && new Date(openToDate)) || defaultOpenToDate;
    const filterTime = getFilterTime({ showTimeSelect, showTimeSelectOnly, hasIncludeIntervals, includeIntervals, selected, openToDate: finalOpenToDate });

    function formatSaveDate(date) {
      if (!date) return null;
      const mDate = moment(date);
      if (!showTimeSelect) {
        return mDate.format('YYYY-MM-DD');
      }
      if (storeIsoDate) {
        return mDate.toISOString();
      }
      if (showTimeSelectOnly) {
        return mDate.format('HH:mm');
      }
      // Keep date using the user's timezone formatting
      return mDate.format('YYYY-MM-DD HH:mm');
    }

    return (
      <FieldContainer {...this.props}>
        <DatePicker
          className="af-string-field"
          // Will use default locale if not defined :)
          locale={lang && lang in locales ? lang : undefined}
          selected={selected}
          name={name}
          isClearable={false}
          onChange={(date) => onChange(name, formatSaveDate(filterTime ? clampToIntervals(date, includeIntervals) : date))}
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelectOnly}
          timeFormat="HH:mm"
          timeIntervals={timeIntervals}
          dateFormat={showTimeSelectOnly ? 'HH:mm' : finalDateFormat}
          placeholderText={placeholderText}
          timeCaption={timeCaption || i18n.t('dates.time')}
          // With hours, the calendar offers the whole days of the intervals and filterTime their hours
          includeDateIntervals={filterTime ? toWholeDays(includeIntervals) : includeIntervals}
          openToDate={finalOpenToDate}
          filterDate={fromToday ? isAfterToday : undefined}
          filterTime={filterTime}
          required={required}
          calendarStartDay={calendarStartDay}
          disabled={disabled || isLocked}
          popperContainer={CalendarContainer}
        />
      </FieldContainer>
    );
  }
}
